import { fetchWithReadRetry } from "./readRetry";
import type { Admin, AdminGateway, Workspace } from "../domain";
import { parseAdmin } from "../auth/policy";
export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, message: string, code = "", public readonly details: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.code = code;
  }
}
export function readRecoveryDelay(error: unknown, attempt: number): number | undefined {
  const transient = error instanceof TypeError ||
    (error instanceof DOMException && error.name === "TimeoutError") ||
    (error instanceof ApiError && [502, 503, 504].includes(error.status));
  return transient ? [5_000, 15_000][attempt] : undefined;
}
let csrfToken = "";
let organizationId = "";
try { organizationId = sessionStorage.getItem("mzn.currentOrganization") || ""; } catch { /* Storage may be disabled. */ }
let contextRequests = new AbortController();
function setOrganization(id: string) {
  if (id !== organizationId) {
    contextRequests.abort();
    contextRequests = new AbortController();
    organizationId = id;
  }
  try { if (id) sessionStorage.setItem("mzn.currentOrganization", id); else sessionStorage.removeItem("mzn.currentOrganization"); } catch { /* Keep a tab-local preference if storage is unavailable. */ }
}
export async function switchOrganization(id: string): Promise<Admin> {
  const previous = organizationId;
  setOrganization(id);
  try { return await currentAdmin(undefined, false); }
  catch (error) { setOrganization(previous); throw error; }
}
export async function request<T>(
  path: string,
  method = "GET",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  // A server response must never turn simulation controls into Production requests.
  if (!__MZN_SIMULATION_TOOLS__ && (/^\/billing\/local/.test(path) || /\/paymentSimulation\//.test(path) || /\/promotionBenefits/.test(path) || /^\/messageSimulation\//.test(path))) {
    throw new ApiError(403, "Simulation tools are unavailable in this environment", "ADMIN_FORBIDDEN");
  }
  const contextSignal = contextRequests.signal;
  const response = await fetchWithReadRetry("/api/admin" + path, {
    method,
    credentials: "same-origin",
    signal: AbortSignal.any([contextSignal, AbortSignal.timeout(15000), ...(signal ? [signal] : [])]),
    headers: {
      Accept: "application/json",
      ...(organizationId ? { "X-Mzn-Organization": organizationId } : {}),
      "X-Mzn-Admin": "1",
      Language: document.documentElement.lang || "ar",
      Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(method !== "GET" && csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }, method === "GET" && /^\/(session|productWorkspaces)(?:\?|$)/.test(path),
  method === "GET" && path.startsWith("/events/"));
  contextSignal.throwIfAborted();
  if (response.status === 401) {
    csrfToken = "";
    setOrganization("");
    window.dispatchEvent(new Event("mzn:session-expired"));
  }
  const payload = (await response.json().catch(() => null)) as {
    success?: boolean;
    data?: T;
    errorCode?: string;
  } | null;
  contextSignal.throwIfAborted();
  if (
    !response.ok ||
    !payload ||
    payload.success !== true ||
    !("data" in payload)
  ) {
    if (response.status === 403 && path !== "/session") window.dispatchEvent(new Event("mzn:access-check"));
    const detail = payload?.data && typeof payload.data === "object" && "message" in payload.data
      ? payload.data.message : undefined;
    throw new ApiError(response.status, typeof detail === "string" && detail.length <= 1000 ? detail : "Admin service request failed", payload?.errorCode, payload?.data && typeof payload.data === "object" ? payload.data as Record<string, unknown> : {});
  }
  return payload.data as T;
}
export async function currentAdmin(signal?: AbortSignal, recoverSelection = true): Promise<Admin> {
  let value: unknown;
  try { value = await request<unknown>("/session", "GET", undefined, signal); }
  catch (error) {
    if (!recoverSelection || !organizationId || !(error instanceof ApiError) || error.status !== 403) throw error;
    setOrganization("");
    value = await request<unknown>("/session", "GET", undefined, signal);
  }
  const admin = parseAdmin(value);
  csrfToken = admin.csrfToken;
  setOrganization(admin.organization?.id || "");
  return admin;
}
export async function signOut(): Promise<void> {
  await request("/auth/logout", "POST", {});
  csrfToken = "";
  setOrganization("");
}
export const gateway: AdminGateway = {
  reports: (input, signal) => request("/reports?" + new URLSearchParams({ page: String(input.page), status: input.status, search: input.search }), "GET", undefined, signal),
  load: (signal) => request<Workspace>("/workspace", "GET", undefined, signal),
  details: (id) => request("/content/details?id=" + encodeURIComponent(id)),
  resolve: (input) => request("/cases/resolve", "POST", input),
  review: (id, decision, reason, requestId, contentVersion) =>
    request("/content/review", "POST", { id, decision, reason, requestId, contentVersion, inspected: true }),
  suspendCustomer: (id, reason, requestId) =>
    request("/customers/suspend", "POST", { id, reason, requestId }),
};
